import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, LogIn, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { InstallButton } from '../components/InstallButton';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, verifyMfaLogin } = useAuth();
  const navigate = useNavigate();

  // MFA step state
  const [mfaStage, setMfaStage] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const continueAfterLogin = (result) => {
    const { user, requires_role_selection } = result;
    toast.success(`Welcome back, ${user.name}!`);
    if (requires_role_selection) { navigate('/select-role'); return; }
    if (user.role === 'medic') navigate('/portal');
    else navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        setMfaStage(true);
        setMfaToken(result.mfa_token);
        setMfaCode('');
      } else {
        continueAfterLogin(result);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await verifyMfaLogin(mfaToken, mfaCode.trim());
      continueAfterLogin(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1ABC9C]">Elara</h1>
          <p className="text-[#C7C7C7] mt-2">Workforce Management System</p>
        </div>

        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
              {mfaStage ? <><ShieldCheck className="w-5 h-5 text-[#1ABC9C]" /> Verify your identity</> : 'Sign In'}
            </CardTitle>
            <CardDescription className="text-[#C7C7C7]">
              {mfaStage
                ? 'Enter the 6-digit code from your authenticator app, or a 10-character backup code.'
                : 'Enter your credentials to access your account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mfaStage ? (
              <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="mfa-verify-form">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm" data-testid="mfa-error">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="mfa-code" className="text-[#F2F2F2]">Verification code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder="123456"
                    className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] text-lg tracking-widest text-center focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                    required
                    data-testid="mfa-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
                  disabled={loading || !mfaCode}
                  data-testid="mfa-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Verify and continue</>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => { setMfaStage(false); setMfaCode(''); setMfaToken(''); setError(''); }}
                  className="w-full text-sm text-[#A1A1AA] hover:text-[#F2F2F2]"
                  data-testid="mfa-cancel-btn"
                >
                  Cancel and return to sign in
                </button>
              </form>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#F2F2F2]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#F2F2F2]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In
                  </>
                )}
              </Button>
            </form>
            )}

            <div className="mt-4 text-center">
              <Link 
                to="/forgot-password" 
                className="text-sm font-medium text-[#1ABC9C] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>

            <div className="mt-4">
              <InstallButton
                variant="outline"
                className="w-full border-white/10 text-[#F2F2F2] hover:bg-white/5"
                label="Install Elara WFM"
                testId="login-install-btn"
              />
            </div>

            <div className="mt-4 text-center text-sm text-[#C7C7C7]">
              Do not have an account?{' '}
              <Link to="/register" className="text-[#1ABC9C] hover:underline" data-testid="register-link">
                Register
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
